import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Footer from './Footer';
import MobileMenu from './MobileMenu';
import './Work.css';
import { RecycleIcon, AIIcon, LightningIcon, DesignIcon, BlockchainIcon } from './Icons';

function Work() {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <div className="work-page">
      <nav className="navbar">
        <div className="nav-container">
          <Link to="/" className="nav-link">Lansah</Link>
          <Link to="/#highlights" className="nav-link">Highlights</Link>
          <Link to="/work" className="nav-link active">Work</Link>
          <a href="https://linkedin.com/in/rashid-lansah" className="nav-link" target="_blank" rel="noopener noreferrer">LinkedIn</a>
          <a href="https://cal.eu/abdul-rashid-lansah-adam-ccloo9/30min" className="nav-link" target="_blank" rel="noopener noreferrer">Schedule a call</a>
        </div>
      </nav>
      <MobileMenu />

      <main className="work-main">
        <div className="work-container">
          <div className="work-grid">

            {/* Product design case studies */}
            {[
              { name: 'Intervia ACA', path: '/aca-case-study', title: 'From Assessment Entry to Useful Results', description: 'Product Engineer. Individual and team assessment journeys, onboarding, organisation setup, invitations, and competency reporting.', image: '/images/case-studies/aca/card-preview.svg' },
              { name: 'Polliplus', path: '/polliplus-case-study', title: 'Connecting Evidence and Field Operations', description: 'Product Engineer. Research, user journeys, and product design across field reporting, intelligence, and workspace administration.', image: '/images/case-studies/polliplus/silence-board.jpg' },
              { name: 'Intervia NSAT', path: '/nsat-case-study', title: 'Designing the Newcomer Settlement Journey', description: 'Product Engineer. Research-informed intake, assessment, worker review, and settlement-plan experiences across three platform surfaces.', image: '/images/case-studies/nsat/landing.jpg' },
            ].map((project) => (
              <Link to={project.path} className="work-card clickable-card" key={project.path}>
                <div className="work-card-content">
                  <div className="work-card-header"><span className="work-card-label">{project.name}</span></div>
                  <h2 className="work-card-title">{project.title}</h2>
                  <p className="work-card-description">{project.description}</p>
                  <div className="work-card-image"><div className="interface-preview"><img src={project.image} alt={`${project.name} product interface`} className="work-interface-image" style={{ objectFit: 'contain', background: '#f5f5f5' }} loading="lazy" /></div></div>
                </div>
              </Link>
            ))}
            {/* Stimulir Card */}
            <Link to="/stimulir-case-study" className="work-card clickable-card" style={{ background: '#0f0f0f', color: '#fff' }}>
              <div className="work-card-content">
                <div className="work-card-header">
                  <span className="work-card-label" style={{ color: 'rgba(255,255,255,0.5)', background: 'rgba(255,255,255,0.08)' }}>
                    <span className="ai-badge-small">AI</span>
                    <span>Stimulir</span>
                  </span>
                </div>
                <h2 className="work-card-title" style={{ color: '#fff' }}>Console, Website & Product UX</h2>
                <p className="work-card-description" style={{ color: 'rgba(255,255,255,0.55)' }}>Product Engineer. UX flows, task review, context gathering, onboarding, and the website connecting product discovery to first use.</p>
                <div className="work-card-image">
                  <div className="interface-preview">
                    <img src="/images/case-studies/stimulir/console-traces.png" alt="Stimulir console showing traces and an agent proposal" className="work-interface-image" style={{ objectFit: 'contain', background: '#111' }} />
                  </div>
                </div>
              </div>
            </Link>

            {/* PayChalk Card */}
            <Link to="/paychalk-case-study" className="work-card paychalk-card clickable-card">
              <div className="work-card-content">
                <div className="work-card-header">
                  <span className="work-card-label paychalk-label">
                    <span>PayChalk</span>
                  </span>
                </div>
                <h2 className="work-card-title">School Fee Collection Made Simple</h2>
                <p className="work-card-description">Designed and built a WhatsApp-first school fee collection platform for Ghana. Instant payments, automated reminders, real-time tracking.</p>

                <div className="work-card-image">
                  <div className="interface-preview">
                    <img src="/images/case-studies/paychalk-hero.png" alt="PayChalk Dashboard" className="work-interface-image" />
                  </div>
                </div>
              </div>
            </Link>

            {/* Jenesys AI Card */}
            <Link to="/case-study" className="work-card jenesys-card clickable-card">
              <div className="work-card-content">
                <div className="work-card-header">
                  <span className="work-card-label jenesys-label">
                    <span className="ai-badge-small">AI</span>
                    <span>Jenesys</span>
                  </span>
                </div>
                <h2 className="work-card-title">AI Bookkeeping Platform</h2>
                <p className="work-card-description">Designed the product experience for an AI bookkeeping platform that achieved $1M ARR and 80% adoption rate.</p>
                
                <div className="work-card-image">
                  <div className="interface-preview">
                    <img src="/images/case-studies/jenesys-dashboard.png" alt="Jenesys AI Dashboard" className="work-interface-image" />
                    <div className="work-interface-overlay">
                      <div className="ai-status-badge">
                        <div className="ai-indicator">AI</div>
                        <span>Live</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Link>

            {/* TumaPay Card */}
            <Link to="/tumapay-case-study" className="work-card tumapay-card clickable-card">
              <div className="work-card-content">
                <div className="work-card-header">
                  <span className="work-card-label tumapay-label">
                    <span>TumaPay</span>
                  </span>
                </div>
                <h2 className="work-card-title">Voice-First Fintech for Non-Literate Users</h2>
                <p className="work-card-description">Designed a voice-first mobile money app using local languages and voice pattern recognition to help non-literate users perform transactions confidently.</p>
                
                <div className="work-card-image">
                  <div className="tumapay-preview">
                    <div className="iphone-mockup-small">
                      <div className="iphone-frame-small">
                        <div className="iphone-notch-small"></div>
                        <div className="iphone-screen-small">
                          <img src="/images/tumapay-placeholder.png" alt="TumaPay Voice Interface" className="tumapay-app-image" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Link>

            {/* ReliefLedger Card */}
            <Link to="/reliefledger-case-study" className="work-card reliefledger-card clickable-card">
              <div className="work-card-content">
                <div className="work-card-header">
                  <span className="work-card-label reliefledger-label">
                    <span className="blockchain-badge-small">
                      <BlockchainIcon size={16} />
                    </span>
                    <span>ReliefLedger</span>
                  </span>
                </div>
                <h2 className="work-card-title">Rebuilding Trust in Humanitarian Aid</h2>
                <p className="work-card-description">Designed a blockchain-powered transparency platform that connects NGOs, field agents, and communities through verifiable proof of delivery.</p>
                
                <div className="work-card-image">
                  <div className="reliefledger-preview">
                    <img src="/images/reliefledger-full-image.svg" alt="ReliefLedger Platform Interface" className="reliefledger-app-image" />
                  </div>
                </div>
              </div>
            </Link>

            {/* Tikiti Card */}
            <div className="work-card tikiti-card">
              <div className="work-card-content">
                <div className="work-card-header">
                  <span className="work-card-label">Tikiti</span>
                </div>
                <h2 className="work-card-title">Event Discovery & Ticketing App</h2>
                <p className="work-card-description">Designed and shipped an event discovery and ticketing app with 1.7k downloads. QR scanning, mobile payments, real-time analytics.</p>
                
                <div className="work-card-image">
                  <div className="tikiti-preview">
                    <img src="/images/tikiti-app-preview.svg" alt="Tikiti App Interface" className="tikiti-app-image" />
                    <div className="tikiti-overlay">
                      <div className="tikiti-badge">
                        <span>🎫</span>
                        <span>Live App</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* BorlaPay Card */}
            <Link to="/borlapay-case-study" className="work-card borlapay-card clickable-card">
              <div className="work-card-content">
                <div className="work-card-header">
                  <span className="work-card-label borlapay-label">
                    <span className="recycle-badge-small">
                      <RecycleIcon size={16} />
                    </span>
                    <span>BorlaPay</span>
                  </span>
                </div>
                <h2 className="work-card-title">Recycle more, earn more</h2>
                <p className="work-card-description">Designed a mobile-first recycling incentive platform that transforms waste management through gamified rewards and community-driven collection.</p>
                
                <div className="work-card-image">
                  <div className="borlapay-preview">
                    <img src="/images/borlapay-full-image.svg" alt="BorlaPay Mobile App Interface" className="borlapay-app-image" />
                  </div>
                </div>
              </div>
            </Link>

            {/* Receipt2Recipe Card */}
            <Link to="/receipt2recipe-case-study" className="work-card receipt2recipe-card clickable-card">
              <div className="work-card-content">
                <div className="work-card-header">
                  <span className="work-card-label receipt2recipe-label">
                    <span className="recipe-badge-small">🍳</span>
                    <span>Receipt2Recipe</span>
                  </span>
                </div>
                <h2 className="work-card-title">Transform receipts into recipes</h2>
                <p className="work-card-description">Designed and shipped an AI-powered app that converts grocery receipts into personalized meal plans, helping users reduce food waste.</p>
                
                <div className="work-card-image">
                  <div className="receipt2recipe-preview">
                    <img src="/images/receipt2recipe-image.png" alt="Receipt2Recipe App Interface" className="receipt2recipe-app-image" />
                  </div>
                </div>
              </div>
            </Link>

            {/* Medium Article Card */}
            <a 
              href="https://medium.com/design-bootcamp/how-i-use-ai-to-supercharge-my-design-process-2ae95b0a7f91"
              target="_blank"
              rel="noopener noreferrer"
              className="work-card article-card clickable-card"
            >
              <div className="work-card-content">
                <div className="work-card-header">
                  <span className="work-card-label">Article</span>
                </div>
                <h2 className="work-card-title">How I Use AI to Supercharge My Design Process</h2>
                <p className="work-card-description">Discover practical AI tools and techniques that can streamline your design workflow, from ideation to prototyping, and learn how to integrate AI seamlessly into your creative process.</p>
                
                <div className="work-card-image">
                  <div className="article-illustration">
                    <div className="ai-tools-showcase">
                      <div className="ai-tool">
                        <div className="tool-icon">
                          <AIIcon size={20} />
                        </div>
                        <span>AI Tools</span>
                      </div>
                      <div className="ai-tool">
                        <div className="tool-icon">
                          <LightningIcon size={20} />
                        </div>
                        <span>Workflow</span>
                      </div>
                      <div className="ai-tool">
                        <div className="tool-icon">
                          <DesignIcon size={20} />
                        </div>
                        <span>Design</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </a>

            {/* Confidence-Based Feedback UI Card */}
            <a 
              href="https://medium.com/design-bootcamp/designing-a-confidence-based-feedback-ui-f5eba0420c8c"
              target="_blank"
              rel="noopener noreferrer"
              className="work-card confidence-feedback-card clickable-card"
            >
              <div className="work-card-content">
                <div className="work-card-header">
                  <span className="work-card-label">Article</span>
                </div>
                <h2 className="work-card-title">Designing a Confidence-Based Feedback UI</h2>
                <p className="work-card-description">A personal design project exploring how interfaces can communicate AI uncertainty. And keep users in control.</p>
                
                <div className="work-card-image">
                  <div className="confidence-illustration">
                    <div className="confidence-elements">
                      <div className="confidence-tag green">
                        <span>95%</span>
                      </div>
                      <div className="confidence-tag yellow">
                        <span>67%</span>
                      </div>
                      <div className="confidence-tag red">
                        <span>45%</span>
                      </div>
                      <div className="ai-badge">
                        <span>AI</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </a>

            {/* Design x Sales Card */}
            <a 
              href="https://medium.com/design-bootcamp/design-x-sales-why-i-joined-demo-calls-as-a-product-designer-9db4a702937b"
              target="_blank"
              rel="noopener noreferrer"
              className="work-card design-sales-card clickable-card"
            >
              <div className="work-card-content">
                <div className="work-card-header">
                  <span className="work-card-label">Article</span>
                </div>
                <h2 className="work-card-title">Design x Sales: Why I Joined Demo Calls</h2>
                <p className="work-card-description">How collaborating with sales teams improved my design process and gave me clearer insight into our users.</p>
                
                <div className="work-card-image">
                  <div className="sales-illustration">
                    <div className="sales-elements">
                      <div className="demo-screen">
                        <div className="screen-content">
                          <div className="feature-highlight"></div>
                          <div className="feature-highlight"></div>
                          <div className="feature-highlight"></div>
                        </div>
                      </div>
                      <div className="sales-badge">
                        <span>Sales</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </a>

            {/* Designing for Dignity Card */}
            <a 
              href="https://medium.com/design-bootcamp/designing-for-dignity-improving-the-low-data-experience-on-mtn-2e5447290266"
              target="_blank"
              rel="noopener noreferrer"
              className="work-card dignity-design-card clickable-card"
            >
              <div className="work-card-content">
                <div className="work-card-header">
                  <span className="work-card-label">Article</span>
                </div>
                <h2 className="work-card-title">Designing for Dignity: Improving MTN Experience</h2>
                <p className="work-card-description">Making low-data users feel seen and in control through thoughtful design that respects their constraints and needs.</p>
                
                <div className="work-card-image">
                  <div className="dignity-illustration">
                    <div className="dignity-elements">
                      <div className="mobile-device">
                        <div className="low-data-indicator">
                          <span>Low Data</span>
                        </div>
                        <div className="data-bars">
                          <div className="bar bar-1"></div>
                          <div className="bar bar-2"></div>
                          <div className="bar bar-3"></div>
                        </div>
                      </div>
                      <div className="mtn-badge">
                        <span>MTN</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </a>

          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default Work;
